'use client'

import {
  useEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
  type RefObject,
} from 'react'
import * as AriakitMenu from '@ariakit/react/menu'
import { ChevronRightIcon } from 'lucide-react'
import Button from '@shared/components/Button/Button'
import * as styles from './Menu.css'
import { Menu } from './Menu'
import { MenuItemContent } from './MenuItem'
import {
  MenuSearchField,
  MenuSearchList,
  MenuSearchProvider,
} from './MenuSearch'

interface SubmenuProps {
  label: ReactNode
  icon?: ElementType
  hint?: ReactNode
  /**
   * Adds a search field. `children` receives the current query, so the
   * caller filters (or loads) the rows it renders, or a `MenuEmpty`.
   */
  search?: { placeholder: string }
  children: ReactNode | ((query: string) => ReactNode)
}

/** A row that opens a nested selection menu to its side. */
export function Submenu({ label, icon, hint, search, children }: SubmenuProps) {
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const focusSearchOnOpen = useRef(false)

  const trigger = (
    <AriakitMenu.MenuItem
      onClick={() => {
        focusSearchOnOpen.current = search !== undefined
      }}
      render={
        <AriakitMenu.MenuButton
          render={<Button variant='flat' className={styles.item} />}
        />
      }
    >
      <MenuItemContent
        icon={icon}
        hint={hint}
        trailing={
          <ChevronRightIcon size={15} className={styles.chevron} aria-hidden />
        }
      >
        {label}
      </MenuItemContent>
    </AriakitMenu.MenuItem>
  )

  if (!search) {
    return (
      <AriakitMenu.MenuProvider placement='right-start'>
        {trigger}
        <Menu layout='selection' gutter={4} shift={-6}>
          {typeof children === 'function' ? children('') : children}
        </Menu>
      </AriakitMenu.MenuProvider>
    )
  }

  return (
    <MenuSearchProvider value={query} onValueChange={setQuery}>
      <AriakitMenu.MenuProvider placement='right-start'>
        {trigger}
        <Menu layout='selection' gutter={4} shift={-6}>
          <FocusOnOpen requested={focusSearchOnOpen} target={searchRef} />
          <MenuSearchField placeholder={search.placeholder} ref={searchRef} />
          <MenuSearchList>
            {typeof children === 'function' ? children(query) : children}
          </MenuSearchList>
        </Menu>
      </AriakitMenu.MenuProvider>
    </MenuSearchProvider>
  )
}

/**
 * Mounts when its menu opens. Opened by a click, a submenu keeps focus on its
 * row, so typing would go nowhere; hover-opening must not move focus at all.
 */
function FocusOnOpen({
  requested,
  target,
}: {
  requested: RefObject<boolean>
  target: RefObject<HTMLInputElement | null>
}) {
  useEffect(() => {
    if (!requested.current) return
    requested.current = false
    requestAnimationFrame(() => target.current?.focus())
  }, [requested, target])
  return null
}
